import moment from 'moment';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  forkJoin,
  interval,
  of,
  throwError,
} from 'rxjs';
import {
  Client,
  Doctor,
  Form,
  FormTemplate,
  isCompletedFormAmendment,
  isFormAmendment,
  PneAppointment,
  PneEmrWidgets,
} from '@nrwl/rx-models';
import {
  FbCheckboxCompositeWidget,
  FbDropdownWidget,
  FbMatrixWidget,
  FbRadioCompositeWidget,
  FbSignatureWidget,
  FbTextareaWidget,
  FbTextboxWidget,
  FormConfig,
} from '@nrwl/rx-form-builder';
import {
  FormService,
  FormTemplateService,
  FormTokensService,
  FormRequestOptions,
  getApiErrorDetail,
} from '@nrwl/rx-service-forms';
import { catchError, map, take, throttle, switchMap } from 'rxjs/operators';

import { AuthService } from '@nrwl/rx-service-auth';
import { DoctorService } from '@nrwl/rx-service-doctor';
import { DomSanitizer } from '@angular/platform-browser';
import { FileConvertService } from '@nrwl/rx-files-helpers-service';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { PDFDocumentProxy, PdfViewerModule } from 'ng2-pdf-viewer';
import { PatientsService } from '@nrwl/rx-service-patients';
import { SentryHandlerService } from '@nrwl/rx-service-sentry';
import { FbAttachmentWidget } from '../models/form-widget-attachment.model';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RxIconModule } from '@nrwl/rx-icon';
import { FormElementComponent } from '../form-element/form-element.component';
import { FbEmrInsuranceInfoWidget } from '../models/form-widget-emr-insurance-info.model';
import { FbEmrMedicalHistoryWidget } from '../models/form-widget-emr-medical-history.model';
import { FbEmrDemographicsWidget } from '../models/form-widget-emr-demographics.model';
import { DiscrepancyDetectionService } from './services/discrepancy-detection.service';
import { WidgetApiService } from './services/widget-api.service';
import { FormSaveService, FormSaveOptions } from '../services/form-save.service';
import { FormMetadataExtractorService } from '../services/form-metadata-extractor.service';
import { FbEmrShotsWidget } from '../models/form-widget-emr-shots.model';
import { FbEmrSoapNotesWidget } from '../models/form-widget-emr-soap-notes.model';
import { FbEmrEquipmentWidget } from '../models/form-widget-emr-equipment.model';
import { FbEmrVitalsWidget } from '../models/form-widget-emr-vitals.model';
import { FbEmrLaserWidget } from '../models/form-widget-emr-laser.model';
import { AppointmentContextService } from './services/appointment-context.service';
import {
  AmendmentChangeTracker,
  AmendmentFieldMarkersService,
  buildAmendmentWidgetId,
} from '../amendments';

@Component({
  selector: 'rx-form-presenter',
  templateUrl: './rx-form-presenter.component.html',
  styleUrls: ['./rx-form-presenter.component.scss'],
  providers: [AppointmentContextService, AmendmentFieldMarkersService],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PdfViewerModule,
    RxIconModule,
    FormElementComponent,
  ],
})
export class RxFormPresenterComponent implements AfterViewInit {
  private pdfDocumentProxy: PDFDocumentProxy;

  @ViewChildren('widgets') widgets: QueryList<any>;
  @ViewChildren('widgets', { read: ElementRef }) widgetsRef: QueryList<any>;
  @ViewChildren('printWidgets') printWidgets: QueryList<any>;
  @ViewChildren('sections') sections: QueryList<any>;
  @ViewChildren('widgets') vcWidgets: QueryList<any>;
  @ViewChild('container', { read: ElementRef }) container: ElementRef;
  @ViewChild('printPages', { read: ElementRef, static: false })
  printPages: ElementRef;

  @HostBinding('style.height.px') hostHeight: number;
  @HostBinding('style.width.px') hostWidth: number;

  private readonly DEFAULT_EMPTY_IMAGE =
    'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

  @Input()
  public suppress403 = false;

  @Input()
  public isRxPortal = false;

  @Input()
  public useDirectApi = false;

  public clinicLogo$ = new BehaviorSubject<any>(null);

  // Buffer to handle incremental treatment_notes updates from iOS
  private accumulatedTreatmentNotes: any[] = [];

  private _config:
    | {
        token: string;
        patientId: number;
        patient: any;
        formId: number;
        formTemplateId: number;
        pdf_file: string;
        wrap: boolean;
        readonly: boolean;
        autoScale: boolean;
        isPreivew: boolean;
        suppress403: boolean;
        skipDoctorProfile?: boolean;
        doctor?: Doctor | null;
        isPatientViewer?: boolean;
        byRemoteId?: boolean;
        treatment_id?: string | number | null;
      }
    | any = null;

  @Input()
  get config() {
    return this._config;
  }
  set config(value) {
    const previousFormId = this._config?.formId;
    const newFormId = value?.formId;

    this._config = value;
    this.autoScale = this._config?.autoScale;
    this.suppress403 = this._config?.suppress403;
    this.wrap = this._config?.wrap;
    this.mode = this._config?.mode || '';
    this.byRemoteId = !!this._config?.byRemoteId;
    this.isPreview = this._config?.isPreview ?? false;

    this.appointmentContextService.syncFromConfig(value);

    if (this._config?.pdf_file) {
      this.loadPdfForm(this._config?.pdf_file);
    } else if (this._config?.formId && this._config?.token && previousFormId !== newFormId) {
      this.loadForm(this._config?.formId);
    } else if (this._config?.formTemplateId && this._config?.token) {
      this.loadFormTemplate(this._config?.formTemplateId, this._config?.patientId);
    }
  }

  @Input() public isPreview = false;
  @Input() public embedded = false;

  private _autoScale;
  @Input()
  get autoScale() {
    return this._autoScale;
  }
  set autoScale(value) {
    this._autoScale = value;
  }

  private _wrap;
  @Input()
  get wrap() {
    return this._wrap;
  }
  set wrap(value) {
    this._wrap = value;
  }

  @Input() mode = '';

  /**
   * Whether fields amended in the loaded version show the amendment indicator.
   */
  @Input()
  public set showAmendmentIndicators(value: boolean) {
    this.amendmentFieldMarkers.setEnabled(!!value);
  }

  @Output() formLoaded = new EventEmitter<boolean>();
  @Output() action: EventEmitter<any> = new EventEmitter<any>();
  @Output() validationError: EventEmitter<{ errorName?: string }> = new EventEmitter<{
    errorName?: string;
  }>();

  private ERROR_PATIENT = 'An error occured while retriving Patient profile.';
  private ERROR_FORM_PARSE = 'An error occured while parsing form data.';
  private ERROR_FORM_CREATE = 'An error occured while creating form.';
  private ERROR_FORM_UPDATE = 'An error occured while updating form.';
  private ERROR_TEMPLATE = 'An error occured while parsing template data.';
  private ERROR_AUTOFILL = 'An error occured while parsing tokens data.';
  private ERROR_AUTOFILL_EXISTING =
    'An error occured while parsing tokens data of an existing form.';

  public ready = new BehaviorSubject<boolean>(true);
  private loadedWidgets = [];
  private loadedInitialWidgets = [];
  public formTemplate: FormTemplate;
  public form: Form;
  public formData: any;
  public formDataOriginal: any;
  private readonly amendmentFieldMarkers = inject(AmendmentFieldMarkersService);
  private readonly amendmentChanges = new AmendmentChangeTracker();
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  public rawFormData: string | null = null;
  public rawFormTemplate: string | null = null;
  public doctor: Doctor;
  public client: Client;
  public totalWidgets = -1;
  public logoLoaded = false;
  public containerWidth = 0;
  public containerHeight = 0;
  public containerPadding = { width: 20, height: 10 };
  public a4ratio = 1.41428571429;
  private widgetSizeSubject = new Subject();
  public warningAreas = [];
  public formDataToPrint: FormConfig;
  public readyToAssemblePrint = false;
  public forceLoadedState = false;
  public hasDiscrepancies = false;
  public discrepancies: any[] = [];
  public saveError: string | null = null;
  public byRemoteId = false;

  public get appointmentId(): string | number | null {
    return this.appointmentContextService.appointmentId;
  }
  public set appointmentId(appointmentId: string | number | null) {
    this.appointmentContextService.setAppointmentId(appointmentId);
  }

  public get treatmentId(): string | number | null {
    return this.appointmentContextService.treatmentId;
  }
  public set treatmentId(treatmentId: string | number | null) {
    this.appointmentContextService.setTreatmentId(treatmentId);
  }

  public get appointment(): PneAppointment | null {
    return this.appointmentContextService.appointment;
  }
  public set appointment(appointment: PneAppointment | null) {
    this.appointmentContextService.setAppointmentValue(appointment);
  }

  public get isSharedWithPatient(): boolean {
    return this.form?.shared_with_patient === true;
  }

  public get isPatientViewerContext(): boolean {
    return this._config?.isPatientViewer === true || this.isRxPortal;
  }

  public get isEmrReadonly(): boolean {
    return this.mode === 'kiosk' || (this.isSharedWithPatient && this.isPatientViewerContext);
  }

  // [rxEventFormSigned] - Signature recieved
  @HostListener('window:rxEventFormSigned', ['$event'])
  rxEventFormSigned(event) {
    // IMPORTANT: Signature handled by fb-signature component
    // console.log('signature received: ', event.data);
  }

  // [rxEventEditImageDone] - Image edited in iOS
  // Handles widget update and incremental treatment_notes collection
  @HostListener('window:rxEventEditImageDone', ['$event'])
  rxEventEditImageDone(event) {
    const detail = event.detail;
    if (!detail) return;

    // 1. Update Widget Visuals
    const widget = this.widgets.find((w) => w.payload?.id === detail.id);
    if (widget) {
      if (widget.properties && widget.properties.value) {
        widget.properties.value.value = detail.data;
        this._cd.detectChanges();
      }
    }

    // 2. Handle Incremental Treatment Notes
    // We append new notes to the local buffer to handle multiple image edits
    if (
      detail.metadata &&
      detail.metadata.treatment_notes &&
      Array.isArray(detail.metadata.treatment_notes)
    ) {
      this.accumulatedTreatmentNotes = [
        ...this.accumulatedTreatmentNotes,
        ...detail.metadata.treatment_notes,
      ];
    }
  }

  // [rxEventFormClosing] - Window closing
  @HostListener('window:rxEventFormClosing', ['$event'])
  rxEventFormClosing(event) {
    if (event.detail && event.detail.save) {
      return this.save(event.detail.kiosk);
    }
  }

  // DEPRICATED: RESERVED
  // [rxEventFormClosing] - Kiosk mode
  @HostListener('window:rxEventKioskMode', ['$event'])
  rxEventKioskMode(event) {
    console.log('kiosk mode: ', event.flag);
  }

  // Resize event, used by print
  @HostListener('window:resize', [])
  private onResize() {
    this.containerWidth = this.container.nativeElement.offsetWidth - this.containerPadding.width;
    this.containerHeight = this.containerWidth * this.a4ratio + this.containerPadding.height;
    if (this.autoScale) {
      this.scale();
    }
  }

  constructor(
    private formService: FormService,
    private templateService: FormTemplateService,
    private authService: AuthService,
    public patientsService: PatientsService,
    public dialog: MatDialog,
    public sanitizer: DomSanitizer,
    public formTokensService: FormTokensService,
    public doctorService: DoctorService,
    public _sentryHandlerService: SentryHandlerService,
    private fileConvertService: FileConvertService,
    private _sanitizer: DomSanitizer,
    public http: HttpClient,
    private _cd: ChangeDetectorRef,
    private formSaveService: FormSaveService,
    public renderer: Renderer2,
    private discrepancyDetectionService: DiscrepancyDetectionService,
    private widgetApiService: WidgetApiService,
    private formMetadataExtractorService: FormMetadataExtractorService,
    private appointmentContextService: AppointmentContextService,
  ) {
    this.trackUserInteractions();
  }

  public scale() {
    if (this.container.nativeElement.parentNode?.parentNode) {
      const parentWidth = this.container.nativeElement.parentNode.parentNode.offsetWidth;
      const parentHeight = this.container.nativeElement.parentNode.parentNode.offsetHeight;
      const scaleRatio =
        parentWidth < this.containerWidth + 50 ? parentWidth / (this.containerWidth + 50) : 1;
      this.renderer.setStyle(this.container.nativeElement, 'transform', `scale(${scaleRatio})`);
      this.renderer.setStyle(this.container.nativeElement, 'transform-origin', `top left`);
      if (this.embedded) {
        const scaledHeight =
          (this.containerWidth * this.a4ratio + this.containerPadding.height) * scaleRatio;
        this.renderer.setStyle(this.container.nativeElement, 'height', `${scaledHeight}px`);
      }
    }
  }

  ngAfterViewInit(): void {
    this.onResize();

    this.widgets.changes.pipe(throttle(() => interval(1000))).subscribe((item) => {
      this.totalWidgets = item.length;
      this.checkReadyState();
      this.displayLoadedTag();
      setTimeout(() => {
        this._cd.detectChanges();
      });
    });

    this.printWidgets.changes.subscribe(() => {
      setTimeout(() => {
        if (!this.isPreview) {
          this.loadedWidgets = this.fixDuplicateIdsInArray(this.loadedWidgets);
        }
        this.displayLoadedTag();
        this._cd.detectChanges();
      });
    });
  }

  public fixDuplicateIdsInArray(loadedWidgets: any[]): any[] {
    const seen = new Set<string>();

    for (const item of loadedWidgets) {
      const id = item?.properties?.id?.value;
      if (!id) continue;

      if (seen.has(id)) {
        item.properties.id.value = 'uid' + Math.random().toString(36).substr(2, 9);
      } else {
        seen.add(id);
      }
    }

    return loadedWidgets;
  }

  onLoaded(pdfDocumentProxy: PDFDocumentProxy | any) {
    this.pdfDocumentProxy = pdfDocumentProxy;
  }

  public setReadonly(flag) {
    this.isPreview = flag;
    this.config.readonly = flag;
    this._cd.detectChanges();
  }
  // Build collection of widgets per page
  // private calcWidgetHeight() {
  //     // RESERVED: Widgets height calculation
  //     this.widgetSizeSubject.pipe(
  //         distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
  //         debounceTime(400)
  //     ).subscribe((widgetSizes: { widget: Widget, height: number }[]) => {

  //         if(widgetSizes.length === 0) {
  //             return;
  //         }

  //         // Clone original FormTemplate
  //         const formData = new FormConfig(JSON.parse(JSON.stringify(this.formData)));
  //         formData.sections = []

  //         // Sections to render for print
  //         const sectionSizes = [{ widgets: [], height: 0}];

  //         // Fit widgets per section (page)
  //         widgetSizes.forEach((item) => {

  //             const section = sectionSizes[sectionSizes.length - 1];

  //             if(this.containerHeight >= section.height + item.height) {
  //                 section.widgets.push(item.widget);
  //                 section.height += item.height;
  //             } else {
  //                 sectionSizes.push({ widgets: [item.widget], height: item.height});
  //             }

  //         });

  //         if(this.readyToAssemblePrint) {
  //             sectionSizes.forEach((item, index) => {
  //                 formData.sections.push(new FormSection({ widgets: item.widgets }));
  //             })

  //             setTimeout(() => {
  //                 this.formData = null;
  //                 this.formDataToPrint = formData;
  //             });
  //         }
  //     });
  // }

  public processPdfFile(pdf_file) {
    this.ready.next(false);
    const promise = new Promise((resolve, reject) => {
      this.http
        .get(pdf_file, { responseType: 'blob' })
        .pipe(
          catchError((error) => {
            // PDF File rendering failed, fallback to form_data rendering approach
            this.fallbackToFormDataRedering();
            this._cd.detectChanges();
            reject(error);
            return EMPTY;
          }),
        )
        .subscribe((result) => {
          if (result) {
            const reader = new FileReader();
            const binaryString = reader.readAsDataURL(result);
            reader.onload = (event: any) => {
              const file = this.fileConvertService.b64toFile(event.target.result);

              this.ready.next(true);
              resolve(true);
              this._cd.detectChanges();
            };
            reader.onerror = (event: any) => {
              console.log('File could not be read: ' + event.target.error.code);
              this.ready.next(true);
              reject(event);
              this._cd.detectChanges();
            };
          } else {
            console.log('RESULT IS EMPTY');
          }
        });
    });
    return promise;
  }

  public fallbackToFormDataRedering() {
    if (this.config.formId > 0) {
      this.loadForm(this.config.formId, true);
    } else {
      this.ready.next(true);
    }
  }

  public loadForm(formId: number, preventRenderingPDF = false) {
    this.ready.next(false);
    this.amendmentFieldMarkers.clear();
    this.formService
      .getByIdV3(formId, true)
      .pipe(take(1))
      .subscribe(
        (form: Form) => {
          this.patientsService
            .getById(form.patient, this.byRemoteId, true)
            .pipe(
              take(1),
              catchError((error, caught: Observable<any>) => {
                this.reportError(this.ERROR_PATIENT);
                console.log(this.ERROR_PATIENT, error);
                return EMPTY;
              }),
            )
            .subscribe((patient) => {
              this.setResolvedPatientId(patient);
              this.form = form;
              this.rawFormData = form.form_data ?? null;
              // Initialize buffer with existing notes to prevent data loss
              this.accumulatedTreatmentNotes = form.metadata?.treatment_notes || [];

              this.appointmentContextService.hydrateFromMetadata(
                form?.metadata,
                form?.template?.is_pne,
              );

              this.doctor = form.author;
              this.client = patient;
              this.loadLogo(this.doctor);

              if (!this.form.form_data && this.form.pdf_file && !preventRenderingPDF) {
                this.processPdfFile(this.form.pdf_file);
                return;
              }

              // Replace form tokens with real data if available
              // WARNING: What if token data changed
              let resolvedFormData = this.rawFormData ?? '';
              try {
                resolvedFormData = this.formTokensService.replace(
                  patient || {},
                  this.doctor || {},
                  this.rawFormData ?? '',
                  this.doctor?.is_eu_datetime_format,
                );
              } catch (autoFillError) {
                this.reportError(this.ERROR_AUTOFILL_EXISTING);
                console.log(this.ERROR_AUTOFILL_EXISTING, autoFillError);
              }

              try {
                const parsedFormData = this.parseFormConfigSource(
                  resolvedFormData,
                  this.ERROR_FORM_PARSE,
                );
                this.formData = new FormConfig(parsedFormData);
                if (!this.formData.id && this.form.id) {
                  this.formData.id = this.form.id;
                }
                this.formDataOriginal = JSON.stringify(this.formData);
                this.amendmentChanges.reset();
                this.syncAmendmentFieldMarkers();
                this.checkForDiscrepancies(patient);
              } catch (e) {
                this.reportError(this.ERROR_FORM_PARSE);
                console.log(this.ERROR_FORM_PARSE, e);
              }

              this.ready.next(true);
              this._cd.detectChanges();
            });
        },
        (error) => {
          this.ready.next(true);
          this.formLoaded.emit(false);
          this._cd.detectChanges();
        },
      );
  }

  public loadLogo(doctor) {
    if (!doctor || !doctor.clinic || !doctor.clinic.logo) {
      return;
    }

    this.fileConvertService.toBase64viaCanvas(
      this.doctor.clinic.logo,
      (result) => {
        this.clinicLogo$.next(this._sanitizer.bypassSecurityTrustResourceUrl(result));
        this._cd.detectChanges();
      },
      (error) => {
        this.clinicLogo$.next(null);
        // No <img> is rendered after conversion fails, so its error event cannot
        // complete readiness. A missing logo must not block the rest of the form.
        this.onLogoLoadError(error);
        this._cd.detectChanges();
      },
    );
  }

  public reportError(error) {
    try {
      (window as any).webkit.messageHandlers.rxEventError.postMessage({
        error: error,
      });
    } catch (e) {
      // console.log('Reporting Error: ', e);
    }
    throw new Error(JSON.stringify(error));
  }

  public loadFormTemplate(formTemplateId, personId?) {
    const promises = [];
    promises.push(this.getFormTemplateObservable(formTemplateId));

    if (personId) {
      promises.push(this.getClientObservable(personId));
    } else {
      promises.push(of(null));
    }

    promises.push(this.getDoctorObservable());

    forkJoin(promises)
      .pipe(take(1))
      .subscribe((results) => {
        const [formTemplate, client, doctor] = results as any as [FormTemplate, Client, Doctor];
        try {
          // Reset buffer for new form
          this.accumulatedTreatmentNotes = [];

          try {
            this.rawFormTemplate = formTemplate.template ?? null;
            formTemplate.template = this.formTokensService.replace(
              client || {},
              doctor || {},
              this.rawFormTemplate ?? '',
              doctor?.is_eu_datetime_format,
            );
          } catch (autoFillError) {
            this.reportError(this.ERROR_AUTOFILL);
            console.log(this.ERROR_AUTOFILL, autoFillError);
          }
          this.setResolvedPatientId(client);
          this.client = client;
          this.doctor = doctor;
          this.loadLogo(this.doctor);
          this.formTemplate = formTemplate;
          if (this.formTemplate && this.formTemplate.is_pne) {
            this.appointmentContextService.hydrateFromStore();
          }
          const parsedTemplate = this.parseFormConfigSource(
            formTemplate.template,
            this.ERROR_TEMPLATE,
          );
          this.formData = new FormConfig(parsedTemplate);
          this.formDataOriginal = JSON.stringify(this.formData);
          this.amendmentChanges.reset();
          this.amendmentFieldMarkers.clear();
        } catch (parseError) {
          this.reportError(this.ERROR_TEMPLATE);
          console.log(this.ERROR_AUTOFILL, parseError);
        }

        this.ready.next(true);
        this._cd.detectChanges();
      });
  }

  private parseFormConfigSource(source: unknown, errorMessage: string): Record<string, unknown> {
    if (typeof source !== 'string' || !source.trim()) {
      throw new Error(errorMessage);
    }

    const parsedSource = JSON.parse(source);
    if (!parsedSource || typeof parsedSource !== 'object' || Array.isArray(parsedSource)) {
      throw new Error(errorMessage);
    }

    return parsedSource as Record<string, unknown>;
  }

  public loadPdfForm(pdfUrl) {
    this.processPdfFile(pdfUrl)
      .then(() => {})
      .catch((error) => {})
      .finally(() => {
        this.ready.next(true);
        this._cd.detectChanges();
      });
  }

  // Save filled form
  public save(kioskMode) {
    if (!this.isValid(kioskMode)) {
      this.validate();
      try {
        (window as any).webkit.messageHandlers.rxEventFormValidationError.postMessage({
          formId: this._config.formId,
          status: 'error',
        });
      } catch (e) {}
      return;
    }

    // For new forms, we need to create them first via the service
    if (!this._config.formId && this._config.patientId > 0 && this._config.formTemplateId > 0) {
      // Create a new form with metadata
      const metadata = this.formMetadataExtractorService.extractMetadata(this);
      const formData = {
        patient: this._config.patientId,
        template: this._config.formTemplateId,
        form_data: JSON.stringify(this.formData),
        status: kioskMode ? 1 : 2,
      };

      // Add metadata to formData if it has relevant data
      if (this.formMetadataExtractorService.hasMetadata(metadata)) {
        formData['metadata'] = metadata;
      }

      // Use direct service for creation since we don't have a form ID yet
      this.createForm(formData);
    } else if (this._config.formId > 0) {
      // For existing forms, use the FormSaveService through saveForm method
      const status = kioskMode ? 1 : 2;
      this.saveForm({ status }).subscribe((result) => {
        if (result.success) {
          // Signal sent via saveForm
        } else {
          this.reportError(this.ERROR_FORM_UPDATE);
          console.log(this.ERROR_FORM_UPDATE, result.error);
          this.saveError = result.error?.message || result.error?.error || this.ERROR_FORM_UPDATE;
        }
        this._cd.detectChanges();
      });
    }
  }

  public getMetadata(): any {
    const signees = this.extractSignatures();
    this.resetOriginalSignatures();

    // MERGE: Start with existing metadata. The amendment block is never inherited: it describes
    // the loaded version, and the version being saved reports its own changed fields below.
    let metadata: any = { ...(this.form?.metadata ?? {}) };
    delete metadata.amendment;

    // Use accumulated notes if available
    if (this.accumulatedTreatmentNotes.length > 0) {
      metadata.treatment_notes = this.accumulatedTreatmentNotes;
    }

    if (signees.length > 0) {
      metadata.signees = signees;
    }

    const medicalHistoryData = this.extractMedicalHistoryMetadata();
    if (medicalHistoryData && medicalHistoryData.medical_history) {
      metadata.medical_history = medicalHistoryData.medical_history;
    }

    const demographicsData = this.extractDemographicsMetadata();
    if (demographicsData) {
      metadata.demographics = demographicsData;
    }

    const medicalNotesData = this.extractMedicalNotesMetadata();
    if (medicalNotesData) {
      metadata = { ...metadata, ...medicalNotesData };
    }

    const laserData = this.extractLaserMetadata();
    if (laserData) {
      metadata = { ...metadata, ...laserData };
    }

    const pneEmrData = this.extractPneEmrMetaData();

    if (pneEmrData) {
      metadata = { ...metadata, ...pneEmrData };
    }

    // An unfinished amendment keeps reporting what it changed so far. Completed amendments are
    // frozen on the backend, so their stored list is left alone.
    if (this.form && isFormAmendment(this.form) && !isCompletedFormAmendment(this.form)) {
      metadata.amendment = {
        changed_field_ids: this.getAmendmentChangedFieldIds(this.form.changed_field_ids),
      };
    }

    return this.appointmentContextService.enrichMetadata(
      metadata,
      this.form?.metadata?.treatment_id,
    );
  }

  public extractPneEmrMetaData(): any {
    if (!this.formData) {
      return null;
    }

    try {
      const metadata: any = {};

      const insuranceMetadata = this.extractInsuranceMetadata();
      if (insuranceMetadata) {
        Object.assign(metadata, insuranceMetadata);
      }

      const shotsMetadata = this.extractShotsMetadata();
      if (shotsMetadata) {
        Object.assign(metadata, shotsMetadata);
      }

      const vitalsMetadata = this.extractVitalsMetadata();
      if (vitalsMetadata) {
        Object.assign(metadata, vitalsMetadata);
      }

      const soapNotesMetadata = this.extractSoapNotesMetadata();
      if (soapNotesMetadata) {
        Object.assign(metadata, soapNotesMetadata);
      }

      const equipmentMetadata = this.extractEquipmentMetadata();
      if (equipmentMetadata) {
        Object.assign(metadata, equipmentMetadata);
      }

      return Object.keys(metadata).length > 0 ? metadata : null;
    } catch (e) {
      console.error('Error extracting PNE EMR metadata:', e);
      return null;
    }
  }

  private extractMedicalHistoryMetadata(): any {
    if (!this.formData) {
      return null;
    }

    try {
      for (const section of this.formData.sections) {
        for (const widget of section.widgets) {
          if (widget.name === 'fb-emr-medical-history') {
            const widgetInstance = this.widgets?.find((w) => w.payload?.id === widget.id);
            if (widgetInstance && typeof widgetInstance.getMetadata === 'function') {
              // Ensure consistent return format - always wrap in medical_history
              const data = widgetInstance.getMetadata();
              if (data !== null && data !== undefined) {
                return { medical_history: data };
              }
            }

            const medicalHistoryWidget = new FbEmrMedicalHistoryWidget(widget);
            if (typeof medicalHistoryWidget.getMetadata === 'function') {
              return { medical_history: medicalHistoryWidget.getMetadata() };
            }
          }
        }
      }
    } catch (e) {
      console.error('Error extracting medical history metadata:', e);
    }

    return null;
  }

  private extractMedicalNotesMetadata(): any {
    if (!this.formData) {
      return null;
    }

    try {
      const treatmentId = this.form?.metadata?.treatment_id;
      for (const section of this.formData.sections) {
        for (const widget of section.widgets) {
          if (widget.name === PneEmrWidgets.FB_EMR_MEDICAL_NOTES) {
            const widgetInstance = this.widgets?.find((w) => w.payload?.id === widget.id);

            if (widgetInstance && typeof widgetInstance.getMetadata === 'function') {
              const data = widgetInstance.getMetadata();

              if (Array.isArray(data?.medical_notes) && data.medical_notes.length > 0) {
                return treatmentId
                  ? { ...data, treatment_id: data.treatment_id ?? treatmentId }
                  : data;
              }
              return null;
            }
          }
        }
      }
    } catch (e) {
      console.error('Error extracting medical notes metadata:', e);
    }

    return null;
  }

  private extractLaserMetadata(): any {
    if (!this.formData) {
      return null;
    }

    try {
      const treatmentId = this.form?.metadata?.treatment_id;
      for (const section of this.formData.sections) {
        for (const widget of section.widgets) {
          if (widget.name === PneEmrWidgets.FB_EMR_LASER) {
            const widgetInstance = this.widgets?.find((w) => w.payload?.id === widget.id);

            if (widgetInstance && typeof widgetInstance.getMetadata === 'function') {
              const data = widgetInstance.getMetadata();

              if (Array.isArray(data?.lasers) && data.lasers.length > 0) {
                return treatmentId
                  ? { ...data, treatment_id: data.treatment_id ?? treatmentId }
                  : data;
              }
              return null;
            }
          }
        }
      }
    } catch (e) {
      console.error('Error extracting laser metadata:', e);
    }

    return null;
  }

  private extractDemographicsMetadata(): any {
    if (!this.formData) {
      return null;
    }

    try {
      for (const section of this.formData.sections) {
        for (const widget of section.widgets) {
          if (widget.name === 'fb-emr-demographics') {
            const widgetInstance = this.widgets?.find((w) => w.payload?.id === widget.id);
            if (widgetInstance && typeof widgetInstance.getMetadata === 'function') {
              const data = widgetInstance.getMetadata();
              if (data !== null && data !== undefined) {
                return data;
              }
            }

            const demographicsWidget = new FbEmrDemographicsWidget(widget);
            if (typeof demographicsWidget.getMetadata === 'function') {
              return demographicsWidget.getMetadata();
            }
          }
        }
      }
    } catch (e) {
      console.error('Error extracting demographics metadata:', e);
    }

    return null;
  }

  private extractInsuranceMetadata(): any {
    for (const section of this.formData.sections) {
      for (const widget of section.widgets) {
        if (widget.name === PneEmrWidgets.FB_EMR_INSURANCE_INFO) {
          const widgetInstance = this.widgets?.find((w) => {
            return w.payload?.id === widget.id;
          });

          return widgetInstance?.getMetadata
            ? widgetInstance.getMetadata()
            : new FbEmrInsuranceInfoWidget(widget).getMetadata?.();
        }
      }
    }

    return null;
  }

  private extractVitalsMetadata(): any {
    for (const section of this.formData.sections) {
      for (const widget of section.widgets) {
        if (widget.name === PneEmrWidgets.FB_EMR_VITALS) {
          const widgetInstance = this.widgets?.find((w) => w.payload?.id === widget.id);

          return widgetInstance?.getMetadata
            ? widgetInstance.getMetadata()
            : new FbEmrVitalsWidget(widget).getMetadata?.();
        }
      }
    }

    return null;
  }

  private extractShotsMetadata(): any {
    for (const section of this.formData.sections) {
      for (const widget of section.widgets) {
        if (widget.name === PneEmrWidgets.FB_EMR_SHOTS) {
          const widgetInstance = this.widgets?.find((w) => w.payload?.id === widget.id);

          return widgetInstance?.getMetadata
            ? widgetInstance.getMetadata()
            : new FbEmrShotsWidget(widget).getMetadata?.();
        }
      }
    }

    return null;
  }

  private extractSoapNotesMetadata(): any {
    for (const section of this.formData.sections) {
      for (const widget of section.widgets) {
        if (widget.name === PneEmrWidgets.FB_EMR_SOAP_NOTES) {
          const widgetInstance = this.widgets?.find((w) => w.payload?.id === widget.id);

          return widgetInstance?.getMetadata
            ? widgetInstance.getMetadata()
            : new FbEmrSoapNotesWidget(widget).getMetadata?.();
        }
      }
    }

    return null;
  }

  private extractEquipmentMetadata(): any {
    for (const section of this.formData.sections) {
      for (const widget of section.widgets) {
        if (widget.name === PneEmrWidgets.FB_EMR_EQUIPMENT) {
          const widgetInstance = this.widgets?.find((w) => w.payload?.id === widget.id);

          return widgetInstance?.getMetadata
            ? widgetInstance.getMetadata()
            : new FbEmrEquipmentWidget(widget).getMetadata?.();
        }
      }
    }

    return null;
  }

  public extractSignatures() {
    if (!this.formData?.sections) {
      return [];
    }

    try {
      return this.formData.sections.flatMap((section) => {
        if (!section.widgets) return [];

        return section.widgets
          .filter((widget) => widget.name === 'fb-signature' && widget.properties?.id?.value)
          .map((widget) => {
            const props = widget.properties;
            const widgetId = props.id.value;
            const signType = props.signType?.value;

            let signeeId = this.getSignatureIdByWidgetId(widgetId);

            if (!signeeId) {
              if (signType === '2' && this.client) {
                signeeId = this.client.id || this.client.remote_id;
              } else if (signType === '1' && this.doctor) {
                signeeId = this.doctor.id;
              }
            }

            const signImage = props.value?.value;
            const isDefault = signImage === this.DEFAULT_EMPTY_IMAGE;
            const isOriginal = signImage === props.value?.original;

            const payload: any = {
              widget_id: widgetId,
              signee_type: signType,
            };

            if (signeeId) {
              payload.signee_id = signeeId;
            }

            if (!signImage || isDefault || isOriginal) {
              return payload;
            }

            payload.sign_image = encodeURIComponent(signImage);
            payload.signed_at = props.signType?.signeeDate || new Date().toISOString();
            payload.full_name = props.signType?.signeeName ?? '';

            return payload;
          })
          .filter(Boolean);
      });
    } catch (e) {
      console.error('ERROR: Signees extraction failed', e);
      return [];
    }
  }

  public getSignatureIdByWidgetId(widgetId: string): number | null {
    const signeeMeta = this.form?.metadata?.signees?.find((signee: any) => {
      return signee.widget_id === widgetId;
    });
    return signeeMeta?.signee_id ?? null;
  }

  public isSignaturesValid(kioskMode = false): boolean {
    if (!this.formData?.sections) {
      return false;
    }

    try {
      let hasSignatureWidget = false;
      let hasRequiredUnsigned = false;

      for (const section of this.formData.sections) {
        if (!section.widgets) continue;

        for (const widget of section.widgets) {
          if (widget.name === 'fb-signature') {
            hasSignatureWidget = true;
            const signatureWidget = new FbSignatureWidget(widget);

            const isValid = signatureWidget.isValid();
            const isRequired =
              typeof signatureWidget.isRequired === 'function'
                ? signatureWidget.isRequired()
                : !!widget.required;

            if (isRequired && isValid) {
              return true;
            }

            if (!isRequired) {
              const val = widget.value;
              const hasValue =
                val !== null &&
                val !== undefined &&
                val !== '' &&
                (typeof val === 'object' ? Object.keys(val).length > 0 : true);

              if (hasValue) {
                return true;
              }
            }

            if (isRequired && !isValid) {
              hasRequiredUnsigned = true;
            }
          }
        }
      }

      if (!hasSignatureWidget) {
        return true;
      }
      return !hasRequiredUnsigned;
    } catch (e) {
      console.error('ERROR: Signatures validation failed', e);
      return true;
    }
  }

  public resetOriginalSignatures() {
    this.formData.sections.forEach((section) => {
      section.widgets.forEach((widget) => {
        if (widget.name === 'fb-signature') {
          widget.properties.value.original = '';
        }
      });
    });
  }

  public clearSignatureValues() {
    if (!this.formData?.sections) return;
    this.formData.sections.forEach((section) => {
      section.widgets?.forEach((widget) => {
        if (widget.name === 'fb-signature') {
          if (widget.properties?.value) {
            widget.properties.value.value = this.DEFAULT_EMPTY_IMAGE;
            widget.properties.value.original = '';
          }
          if (widget.properties?.signType) {
            widget.properties.signType.signeeDate = null;
            widget.properties.signType.signeeName = null;
          }
        }
      });
    });
  }

  public patchSigneeIds(signees: Array<{ signee_id: number; widget_id: string }>) {
    if (!this.form || !Array.isArray(signees)) return;
    this.form = {
      ...this.form,
      metadata: {
        ...(this.form.metadata ?? {}),
        signees,
      },
    };
  }

  private createForm(formData) {
    this.formService
      .create(formData)
      .pipe(
        take(1),
        catchError((err: any) => {
          return of(err);
        }),
      )
      .subscribe((result) => {
        // TODO: Check result if ERROR
        if (result && !result.error) {
          this.saveError = null;
          this._config.formId = result.id;

          try {
            (window as any).webkit.messageHandlers.rxEventFormSaved.postMessage({
              formId: result.id,
              status: 'saved',
              metadata: this.getMetadata(),
            });
          } catch (e) {
            this._sentryHandlerService.handleError('RxEventFormSaved failed: ' + JSON.stringify(e));
          }
        } else {
          this.reportError(this.ERROR_FORM_CREATE);
          console.log(this.ERROR_FORM_CREATE, result.error);
          this.saveError = result.error?.message || result.error?.error || this.ERROR_FORM_CREATE;
        }
        this._cd.detectChanges();
      });
  }

  private updateForm(formId: number, formData: any) {
    this.formService
      .update(formId, formData)
      .pipe(
        catchError((err: any) => {
          return of(err);
        }),
        take(1),
      )
      .subscribe((result) => {
        // TODO: Check result if ERROR
        if (result && !result.error) {
          this.saveError = null;
          try {
            (window as any).webkit.messageHandlers.rxEventFormSaved.postMessage({
              formId: result.id,
              status: 'saved',
              metadata: this.getMetadata(),
            });
          } catch (e) {}
        } else {
          this.reportError(this.ERROR_FORM_UPDATE);
          console.log(this.ERROR_FORM_UPDATE, result.error);
          this.saveError = result.error?.message || result.error?.error || this.ERROR_FORM_UPDATE;
        }
        this._cd.detectChanges();
      });
  }

  public checkReadyState() {
    if (this.loadedInitialWidgets.length === this.totalWidgets) {
      this.readyToAssemblePrint = true;
      this.widgetSizeSubject.next(null);
      this._cd.detectChanges();
    }
  }

  // Make sure all widgets loaded
  public onWidgetReady($event, widget) {
    if ($event !== true || this.loadedWidgets.some((item) => item.id === widget.id)) {
      return;
    }

    this.loadedWidgets.push(widget);
    this.displayLoadedTag();
    this.ready.next(true);
    this._cd.markForCheck();
  }

  public onActionEvent($event) {
    if ($event?.type === 'validation-error' && $event?.data?.errorName) {
      this.validationError.emit($event.data);
    }
    this.action.emit($event);
  }

  public onInsuranceDiscrepanciesDetected(discrepancies: any[]) {
    if (discrepancies.length > 0) {
      this.hasDiscrepancies = true;
      const existingTypes = new Set(
        discrepancies.map((d) => d.type || (d.insuranceName ? 'insurance' : 'medical')),
      );
      const filteredExisting = (this.discrepancies || []).filter((d) => {
        const type = d.type || (d.insuranceName ? 'insurance' : 'medical');
        return !existingTypes.has(type);
      });
      this.discrepancies = [...filteredExisting, ...discrepancies];
      this._cd.detectChanges();
    }
  }

  // Make sure logo loaded
  public onLogoLoaded($event) {
    this.logoLoaded = true;
    this.displayLoadedTag();
  }

  public onLogoLoadError($event) {
    let errorMessage = 'Logo failed to load. ';
    this.logoLoaded = true;

    if (this.form && this.form.id > 0) {
      errorMessage += 'FormID:' + this.form.id;
    } else if (this.formTemplate && this.formTemplate.id > 0) {
      errorMessage += 'FormTemplateID:' + this.formTemplate.id;
    }
    this._sentryHandlerService.handleError(errorMessage);

    this.displayLoadedTag();
  }

  public hasLogo() {
    return this.doctor?.clinic?.logo && this.formData?.properties?.showHeader?.value;
  }

  public hasChanges() {
    if (
      this.formData?.sections?.every((section) =>
        section.widgets?.every((widget) => widget.name === 'fb-richedit'),
      )
    ) {
      return true;
    }
    return JSON.stringify(this.formData) !== this.formDataOriginal;
  }

  public getFormDataJson() {
    return JSON.stringify(this.formData);
  }

  /**
   * Ids of the fields the user changed since the form was loaded, merged with
   * `storedChangedFieldIds` for a form that already tracks amended fields of its own. Values a
   * widget loaded by itself, before the user touched it, do not count.
   */
  public getAmendmentChangedFieldIds(storedChangedFieldIds: readonly string[] = []): string[] {
    const changed = this.amendmentChanges.changedFieldIds(this.formData);

    return [...new Set([...storedChangedFieldIds, ...changed])].sort();
  }

  /**
   * Amendment id of the widget at this position, as the template tags its container.
   */
  public getAmendmentWidgetId(sectionIndex: number, widgetIndex: number): string {
    return buildAmendmentWidgetId(sectionIndex, widgetIndex);
  }

  /**
   * Remembers each widget as it stands when the user first presses a key or a pointer in it, so
   * later changes can be told apart from what the widget loaded by itself. An interaction outside
   * the widgets remembers all of them.
   */
  private trackUserInteractions(): void {
    const onInteraction = (event: Event) => this.onUserInteraction(event);

    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('pointerdown', onInteraction, true);
      document.addEventListener('keydown', onInteraction, true);
    });
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('pointerdown', onInteraction, true);
      document.removeEventListener('keydown', onInteraction, true);
    });
  }

  private onUserInteraction(event: Event): void {
    if (!this.formData?.sections) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const container = target?.closest('[data-amendment-widget]');
    const widgetId =
      container && this.hostElement.nativeElement.contains(container)
        ? container.getAttribute('data-amendment-widget')
        : null;

    this.amendmentChanges.capture(this.formData, widgetId ?? undefined);
  }

  /**
   * Metadata for a new amendment of the loaded version: the regular metadata plus the fields
   * the user changed since the version was loaded.
   */
  public getAmendmentMetadata(): Record<string, unknown> {
    return {
      ...(this.getMetadata() ?? {}),
      amendment: { changed_field_ids: this.getAmendmentChangedFieldIds() },
    };
  }

  /**
   * Marks the fields the loaded version reports as amended so the widgets can show the indicator.
   */
  private syncAmendmentFieldMarkers(): void {
    this.amendmentFieldMarkers.setMarkers(this.formData, this.form?.changed_field_ids);
  }

  /**
   * Save the form with optional status update
   * This method centralizes form saving logic including:
   * - Validation
   * - Metadata extraction
   * - API calls via FormSaveService
   * - Event emission for parent components
   *
   * @param options - Save options including status and bulk IDs
   * @returns Observable of save result
   */
  public saveForm(options: FormSaveOptions = {}): Observable<any> {
    // Call validate first to set isSubmitted flag on widgets
    this.validate();

    // Then check if form is valid
    if (!this.isValid(false)) {
      const error = { success: false, error: 'Form validation failed' };
      this.formSubmitted.emit(error);
      return of(error);
    }

    const formId = this._config?.formId;
    const patientId = this._config?.patientId;
    const templateId = this._config?.formTemplateId;

    if (!formId && (!patientId || !templateId)) {
      const error = { success: false, error: 'Missing required IDs for form creation' };
      this.formSubmitted.emit(error);
      return of(error);
    }

    // Get form data JSON string
    const formDataJson = this.getFormDataJson();

    // Handle new form creation
    if (!formId || formId === 0) {
      const metadata = this.formMetadataExtractorService.extractMetadata(this);
      const createFormData = {
        patient: patientId,
        template: templateId,
        form_data: formDataJson,
        status: options.status || 2,
      };

      if (this.formMetadataExtractorService.hasMetadata(metadata)) {
        createFormData['metadata'] = metadata;
      }

      return this.formService.create(createFormData).pipe(
        map((result) => {
          if (result && !result.error) {
            this.saveError = null;
            // Update config with new form ID for future saves
            this._config.formId = result.id;

            // Try to send iOS event
            try {
              (window as any).webkit.messageHandlers.rxEventFormSaved.postMessage({
                formId: result.id,
                status: 'saved',
                metadata: this.getMetadata(),
              });
            } catch (e) {
              // Ignore iOS event errors in browser
            }

            const successResult = {
              success: true,
              formId: result.id,
              data: result,
              options,
            };
            this.formSubmitted.emit(successResult);
            return successResult;
          } else {
            this.saveError = result.error?.message || result.error?.error || 'Form creation failed';
            const errorResult = {
              success: false,
              error: this.saveError,
            };
            this.formSubmitted.emit(errorResult);
            return errorResult;
          }
        }),
        catchError((error) => {
          this.saveError = getApiErrorDetail(error) || 'Form creation failed';
          const errorResult = { success: false, error: this.saveError };
          this.formSubmitted.emit(errorResult);
          return of(errorResult);
        }),
      );
    }

    // Use direct API based on input configuration
    const directApiOptions = { ...options, useDirectApi: this.useDirectApi };
    return this.formSaveService
      .saveForm(
        formId,
        formDataJson,
        this, // Pass this presenter component for metadata extraction
        directApiOptions,
      )
      .pipe(
        map((result) => {
          // Clear discrepancies after successful save
          if (result.success) {
            this.hasDiscrepancies = false;
            this.discrepancies = [];
            // Update the saved form data to the current form data
            // so future discrepancy checks compare against the newly saved data
            if (this.formData) {
              this.form = { ...this.form, form_data: formDataJson, status: options.status || 2 };
            }

            // Send event to iOS from here (centralized)
            try {
              (window as any).webkit.messageHandlers.rxEventFormSaved.postMessage({
                formId: formId,
                status: 'saved',
                metadata: this.getMetadata(),
              });
            } catch (e) {
              // Ignore iOS event errors in browser
            }
          }

          // Emit event for parent components
          this.formSubmitted.emit({
            ...result,
            formId,
            options,
          });
          return result;
        }),
        catchError((error) => {
          const errorResult = { success: false, error, formId };
          this.formSubmitted.emit(errorResult);
          return of(errorResult);
        }),
      );
  }

  /**
   * Save form with status update
   * Convenience method for saving with status change
   *
   * @param status - New status value
   * @param bulkIds - IDs for bulk status update
   * @param storeContext - Which store context to use (library or inbox)
   * @returns Observable of save result
   */
  public saveFormWithStatus(
    status: number,
    bulkIds?: number[],
    storeContext: 'library' | 'inbox' = 'library',
  ): Observable<any> {
    // Call validate first to set isSubmitted flag and show validation errors
    this.validate();

    // If this is a template-driven flow (no form yet), create the form before updating status
    if (!this._config.formId && this._config.patientId > 0 && this._config.formTemplateId > 0) {
      // check validation result explicitly for template-driven forms
      // for others, it is checked inside saveForm
      if (!this.isValid(false)) {
        const error = { success: false, error: 'Form validation failed' };
        this.formSubmitted.emit(error);
        return of(error);
      }

      const metadata = this.formMetadataExtractorService.extractMetadata(this);
      const createPayload = {
        patient: this._config.patientId,
        template: this._config.formTemplateId,
        form_data: JSON.stringify(this.formData),
        status,
      } as any;

      if (this.formMetadataExtractorService.hasMetadata(metadata)) {
        createPayload.metadata = metadata;
      }

      return this.formService.create(createPayload).pipe(
        take(1),
        map((result) => {
          if (result && !result.error) {
            this.saveError = null;
            this._config.formId = result.id;

            try {
              (window as any).webkit.messageHandlers.rxEventFormSaved.postMessage({
                formId: result.id,
                status: 'saved',
                metadata: this.getMetadata(),
              });
            } catch (e) {}

            const successResult = {
              success: true,
              formId: result.id,
              data: result,
              options: { status, bulkIds, storeContext },
            };
            this.formSubmitted.emit(successResult);
            return successResult;
          }

          this.saveError = result?.error?.message || result?.error?.error || this.ERROR_FORM_CREATE;
          const errorResult = { success: false, error: this.saveError };
          this.formSubmitted.emit(errorResult);
          return errorResult;
        }),
        catchError((error) => {
          this.saveError = getApiErrorDetail(error) || this.ERROR_FORM_CREATE;
          const errorResult = { success: false, error: this.saveError };
          this.formSubmitted.emit(errorResult);
          return of(errorResult);
        }),
      );
    }

    return this.saveForm({
      status,
      bulkIds,
      storeContext,
    }).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  // Display loaded tag to signal backend of load completion
  public displayLoadedTag() {
    if (this.loadedWidgets.length === this.printWidgets.length) {
      setTimeout(() => {
        if (this.hasLogo() && !this.logoLoaded) {
          // Wait for logo to load
          // TODO: Improve error handling
          // console.log('Waiting for logo to load...');
        } else {
          this.formLoaded.emit(true);
          this.ready.next(true);
        }
      });
    }
  }

  public getToday() {
    return moment().format('MMMM DD, YYYY');
  }

  public validate() {
    this.printWidgets.forEach((widget) => {
      widget.isSubmitted = true;
      this._cd.detectChanges();
    });
  }

  public isValid(isKiosk = false) {
    let valid = true;
    this.formData.sections.forEach((section) => {
      if (valid) {
        section.widgets.forEach((widget) => {
          if (valid) {
            let tmpWidget;
            switch (widget.name) {
              case 'fb-textbox-composite': {
                tmpWidget = new FbTextboxWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-textarea-composite': {
                tmpWidget = new FbTextareaWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-radio-composite': {
                tmpWidget = new FbRadioCompositeWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-dropdown': {
                tmpWidget = new FbDropdownWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-checkbox-composite': {
                tmpWidget = new FbCheckboxCompositeWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              // See isSignatureValid()
              // case 'fb-signature': {
              //     tmpWidget = new FbSignatureWidget(
              //         widget,
              //         !isKiosk,
              //     );
              //     valid = tmpWidget.isValid(!isKiosk);
              //     break;
              // }
              case 'fb-matrix': {
                tmpWidget = new FbMatrixWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-attachment': {
                tmpWidget = new FbAttachmentWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-emr-insurance-info': {
                tmpWidget = new FbEmrInsuranceInfoWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-emr-medical-history': {
                tmpWidget = new FbEmrMedicalHistoryWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-emr-demographics': {
                tmpWidget = new FbEmrDemographicsWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-emr-shots': {
                tmpWidget = new FbEmrShotsWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-emr-laser': {
                tmpWidget = new FbEmrLaserWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-emr-vitals': {
                tmpWidget = new FbEmrVitalsWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
              case 'fb-emr-equipment': {
                tmpWidget = new FbEmrEquipmentWidget(widget);
                valid = tmpWidget.isValid();
                break;
              }
            }
          }
        });
      }
    });

    if (valid) {
      valid = this.isSignaturesValid(isKiosk);
    }
    return valid;
  }

  private checkForDiscrepancies(patient: any) {
    // TODO: Move Demographics discrepancy detection to fb-emr-demographics component

    if (!this.form || !this.form.id || !patient) {
      return;
    }

    const hasDemographicsWidget = this.formData?.sections?.some((section) =>
      section.widgets?.some((widget) => widget.name === 'fb-emr-demographics'),
    );

    if (!hasDemographicsWidget) {
      return;
    }

    const patientId = patient.remote_id || patient.id;
    if (!patientId) {
      return;
    }
    if (!this.isPreview) {
      this.widgetApiService
        .getCustomerById(patientId)
        .pipe(catchError(() => of(null)))
        .subscribe((customer: any) => {
          if (customer) {
            const demographicsDiscrepancies =
              this.discrepancyDetectionService.detectDemographicsDiscrepancies(
                this.form.form_data,
                customer,
              );

            if (demographicsDiscrepancies.length > 0) {
              this.hasDiscrepancies = true;
              this.discrepancies = [...(this.discrepancies || []), ...demographicsDiscrepancies];
              this._cd.detectChanges();
            }
          }
        });
    }
  }

  public getDoctorObservable() {
    if (this._config?.skipDoctorProfile) {
      return of(this._config.doctor ?? null);
    }

    return this.authService.whoAmI(this.suppress403).pipe(catchError((err: any) => of(null)));
  }

  public getFormTemplateObservable(templateId) {
    return this.templateService
      .getFormTemplateById(templateId)
      .pipe(map((res: any) => new FormTemplate(res)));
  }

  public getClientObservable(personId: number) {
    return this.patientsService.getById(personId, this.byRemoteId, false).pipe(
      map((patient) => {
        this.setResolvedPatientId(patient);
        return patient;
      }),
      catchError((err: any) => {
        this.reportError(this.ERROR_PATIENT);
        console.log(this.ERROR_PATIENT, err);
        return of(null);
      }),
    );
  }

  private setResolvedPatientId(patient: any) {
    if (this.byRemoteId && patient?.id) {
      this._config.patientId = patient.id;
    }
  }

  // TODO: Prompt PDF for download using ng2-pdf-viewer
  download() {
    this.pdfDocumentProxy.getData().then((u8) => {
      const blob = new Blob([u8.buffer], {
        type: 'application/pdf',
      });
      if (window.navigator && (<any>window.navigator).msSaveOrOpenBlob) {
        (<any>window.navigator).msSaveOrOpenBlob(blob, 'form.pdf');
      } else {
        const url = URL.createObjectURL(blob);
        this.openLink(url);
        setTimeout(function () {
          window.URL.revokeObjectURL(url);
        }, 5000);
      }
    });
  }

  private openLink(url: string) {
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = url;
    a.download = 'Certificate.pdf';
    a.click();
    document.body.removeChild(a);
  }

  // RESERVED: SIGNATURE LOGIC
  // STATUS = EnumChoices((
  //     (1, 'Audit Needed', 'AUDIT_NEEDED'),
  //     (2, 'Incomplete', 'INCOMPLETE'),
  //     (3, 'Review - Director', 'REVIEW_DIRECTOR'),
  //     (4, 'Review - Supervisor', 'REVIEW_SUPERVISOR'),
  //     (5, 'Complete', 'COMPLETE'),
  // ))

  @Output() formSubmitted = new EventEmitter<any>();

  public submitForm(options: FormSaveOptions = {}): Observable<any> {
    if (!this.formData || !this.formData.id) {
      console.error('No form data or form ID available');
      return of({ success: false, error: 'No form data available' });
    }

    return this.formSaveService.submitFromPresenter(this, this.formData.id, options);
  }

  public submitFormWithStatus(status: number, bulkIds?: number[]): Observable<any> {
    const options: FormSaveOptions = {
      status,
      bulkIds: bulkIds || [this.formData?.id],
    };

    return this.submitForm(options);
  }

  public saveFormData(): Observable<any> {
    return this.submitForm({ skipMetadata: false });
  }

  public bulkUpdateForms(
    data: {
      ids: number[];
      status: number;
      shared_with_patient?: boolean;
    },
    options: FormRequestOptions = {},
  ): Observable<any> {
    if (!data.ids || data.ids.length === 0) {
      return of({ success: false, error: 'No form IDs provided' });
    }

    return this.formService.updateBulk(data, options).pipe(
      map((result) => {
        // Send success event to mobile if needed
        try {
          if ((window as any).webkit?.messageHandlers?.rxEventFormsCompleted) {
            (window as any).webkit.messageHandlers.rxEventFormsCompleted.postMessage({
              formIds: data.ids,
              status: 'completed',
            });
          }
        } catch (e) {
          console.error('Failed to send mobile event', e);
        }

        return { success: true, result, data };
      }),
      catchError((error) => {
        this.saveError = getApiErrorDetail(error) || 'Failed to update forms';
        return of({ success: false, error, data });
      }),
    );
  }

  public saveAndBulkUpdate(
    status: number,
    bulkIds: number[],
    saveOptions: FormSaveOptions = {},
  ): Observable<any> {
    return this.saveForm(saveOptions).pipe(
      switchMap((saveResult) => {
        if (!saveResult.success) {
          return of(saveResult);
        }

        return this.bulkUpdateForms({
          ids: bulkIds,
          status: status,
          shared_with_patient: false,
        }).pipe(
          map((bulkResult) => ({
            success: bulkResult.success,
            saveResult,
            bulkResult,
          })),
        );
      }),
    );
  }

  private handleSubmissionResult(result: any): void {
    if (result.success) {
      this.formSubmitted.emit({
        success: true,
        formId: this.formData?.id,
        payload: result.payload,
      });
    } else {
      this.formSubmitted.emit({
        success: false,
        error: result.error,
      });
    }
  }
}
